import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Upload, Image, Loader2, Trash2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export default function AdminHeroSettings() {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  
  const [heroSettings, setHeroSettings] = useState({
    hero_image_url: null,
    updated_at: null
  });
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    fetchHeroSettings();
  }, []);

  const fetchHeroSettings = async () => {
    try {
      const res = await fetch(`${API}/api/admin/hero-settings`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setHeroSettings(data);
      }
    } catch (err) {
      console.error('Failed to fetch hero settings:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async (file) => {
    if (!file) return;

    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      toast.error('File too large. Maximum size is 10MB');
      return;
    }

    const allowedTypes = ['image/png', 'image/jpeg', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Invalid file type. Please upload PNG, JPEG, or WebP');
      return;
    }

    setUploading(true);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const res = await fetch(`${API}/api/admin/hero-settings/upload`, {
        method: 'POST',
        credentials: 'include',
        body: formData
      });

      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.detail || 'Upload failed');
      }

      const data = await res.json();
      setHeroSettings({
        hero_image_url: data.hero_image_url,
        updated_at: data.updated_at
      });
      
      toast.success('Hero image uploaded successfully');
    } catch (err) {
      toast.error(err.message);
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to remove the hero image?')) return;

    try {
      const res = await fetch(`${API}/api/admin/hero-settings/delete`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.detail || 'Delete failed');
      }

      setHeroSettings({
        hero_image_url: null,
        updated_at: null
      });
      
      toast.success('Hero image removed');
    } catch (err) {
      toast.error(err.message);
    }
  };

  const getFullImageUrl = (url) => {
    if (!url) return null;
    if (url.startsWith('http')) return url;
    if (url.startsWith('/uploads')) return `${API}/api${url}`;
    if (url.startsWith('/')) return `${API}${url}`;
    return `${API}/${url}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#F8FAFD] to-[#E8EEF3] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#FF9E00]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#F8FAFD] to-[#E8EEF3]">
      {/* Header */}
      <div className="bg-white border-b border-[#E8EEF3]">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <Button
            onClick={() => navigate('/admin')}
            variant="ghost"
            className="text-[#4A5E75] hover:text-[#1E3A5C] hover:bg-[#E8EEF3] mb-4"
            data-testid="back-to-admin-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Admin
          </Button>
          <div className="flex items-center gap-3">
            <div className="p-3 bg-[#FF9E00]/10 rounded-xl">
              <Image className="w-6 h-6 text-[#FF9E00]" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-[#1E3A5C]">Hero Image Settings</h1>
              <p className="text-[#4A5E75] text-sm">Upload and manage the landing page hero image</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 py-8">
        {/* Hero Image Upload */}
        <div className="bg-white rounded-2xl border border-[#E8EEF3] p-6 mb-6">
          <h2 className="text-lg font-semibold text-[#1E3A5C] mb-2">Landing Page Hero Image</h2>
          <p className="text-sm text-[#4A5E75] mb-4">
            This image appears on the right side of the landing page hero section. 
            Recommended: A clinical, professional image (e.g., healthcare worker with stethoscope).
          </p>

          <div className="flex flex-col lg:flex-row items-start gap-6">
            {/* Preview */}
            <div className="w-full lg:w-96 aspect-[4/3] bg-[#192A3F] rounded-xl flex items-center justify-center overflow-hidden">
              {heroSettings.hero_image_url ? (
                <img 
                  src={getFullImageUrl(heroSettings.hero_image_url)} 
                  alt="Hero" 
                  className="w-full h-full object-cover"
                  data-testid="hero-image-preview"
                />
              ) : (
                <div className="text-center text-white/40 p-6">
                  <Image className="w-12 h-12 mx-auto mb-3" />
                  <p className="text-sm">No hero image uploaded</p>
                  <p className="text-xs mt-1">A default stock image will be shown</p>
                </div>
              )}
            </div>

            {/* Upload Controls */}
            <div className="flex-1">
              <input
                type="file"
                ref={fileInputRef}
                className="hidden"
                accept="image/png,image/jpeg,image/webp"
                onChange={(e) => handleUpload(e.target.files?.[0])}
                data-testid="hero-file-input"
              />
              
              <div className="space-y-3">
                <Button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  className="bg-[#FF9E00] hover:bg-[#E88D00] text-white"
                  data-testid="upload-hero-btn"
                >
                  {uploading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Uploading...
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4 mr-2" />
                      Upload Hero Image
                    </>
                  )}
                </Button>

                {heroSettings.hero_image_url && (
                  <Button
                    variant="outline"
                    onClick={handleDelete}
                    className="text-red-500 border-red-200 hover:bg-red-50"
                    data-testid="delete-hero-btn"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Remove Image
                  </Button>
                )}
              </div>

              <div className="mt-4 p-4 bg-slate-50 rounded-lg">
                <p className="text-xs text-slate-500 font-medium mb-2">Image Guidelines:</p>
                <ul className="text-xs text-slate-500 space-y-1">
                  <li>• Supported formats: PNG, JPEG, WebP</li>
                  <li>• Maximum file size: 10MB</li>
                  <li>• Recommended dimensions: 800x600px or larger</li>
                  <li>• Aspect ratio: 4:3 or similar works best</li>
                  <li>• Content: Clinical, professional healthcare imagery</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Preview in Context */}
        <div className="bg-white rounded-2xl border border-[#E8EEF3] p-6">
          <h2 className="text-lg font-semibold text-[#1E3A5C] mb-4">Preview in Landing Page Context</h2>
          
          <div className="bg-[#192A3F] rounded-xl p-6 overflow-hidden">
            <div className="flex flex-col lg:flex-row items-center gap-6">
              {/* Left side - text */}
              <div className="flex-1">
                <h3 className="text-2xl font-bold text-white mb-2">
                  Test Your <span className="text-[#FF9E00]">Clinical Reasoning</span>—Not Just Your Memory
                </h3>
                <p className="text-white/60 text-sm mb-4">
                  Adaptive quizzes, case scenarios, and clinical challenges designed to build decision-making confidence.
                </p>
                <div className="flex gap-3">
                  <span className="bg-[#FF9E00] text-white px-4 py-2 rounded-full text-sm font-medium">Start a Study Session</span>
                  <span className="bg-[#264B73] text-white px-4 py-2 rounded-full text-sm font-medium">Browse Topics</span>
                </div>
              </div>
              
              {/* Right side - hero image */}
              <div className="w-48 h-36 bg-white/10 rounded-xl overflow-hidden flex items-center justify-center">
                {heroSettings.hero_image_url ? (
                  <img 
                    src={getFullImageUrl(heroSettings.hero_image_url)} 
                    alt="Hero Preview" 
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="text-white/30 text-xs text-center p-2">
                    <Image className="w-6 h-6 mx-auto mb-1" />
                    Hero Image
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {heroSettings.updated_at && (
          <p className="text-xs text-slate-400 text-center mt-6">
            Last updated: {new Date(heroSettings.updated_at).toLocaleString()}
          </p>
        )}
      </div>
    </div>
  );
}
